import { StandardEvents } from '@shopify/events';

/**
 * Product-page Buy Now controller for L.B.C.
 * Keeps Add to Cart on Horizon's native AJAX/cart-drawer path while Buy Now
 * sends the resolved selected variant directly into native Shopify checkout.
 */
class LbcBuyNow extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Promise<any> | null} */
  #pendingVariantChange = null;

  /** @type {string | null} */
  #resolvedVariantId = null;

  connectedCallback() {
    const button = this.#button;
    const productForm = this.#productForm;
    if (!button || !productForm) return;

    this.#resolvedVariantId = this.#variantInput?.value || null;

    const { signal } = this.#abortController;
    const scope = productForm.closest('.shopify-section, dialog, product-card') || document;

    button.addEventListener('click', this.#handleClick, { signal });
    scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.lbc-buy-now-button'));
  }

  get #productForm() {
    return /** @type {HTMLElement | null} */ (this.closest('product-form-component'));
  }

  get #variantInput() {
    return /** @type {HTMLInputElement | null} */ (this.#productForm?.querySelector('input[name="id"]') || null);
  }

  #onProductSelect = (event) => {
    const button = this.#button;
    const productForm = this.#productForm;
    const promise = event?.promise;
    if (!button || !productForm || !promise) return;

    const sourceCard = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (sourceCard && !sourceCard.contains(productForm)) return;

    this.#pendingVariantChange = promise;
    button.disabled = true;

    promise
      .then(({ detail }) => {
        if (promise !== this.#pendingVariantChange) return;

        const currentProductId = productForm.dataset.productId;
        if (detail?.productId && currentProductId && detail.productId !== currentProductId && !detail.newProduct) {
          return;
        }

        if (detail?.newProduct?.id) {
          productForm.dataset.productId = detail.newProduct.id;
          this.dataset.productId = detail.newProduct.id;
        }

        const resource = detail?.resource;
        if (!resource || resource.available === false || resource.id == null) {
          this.#resolvedVariantId = null;
          button.disabled = true;
          return;
        }

        this.#resolvedVariantId = String(resource.id);
        button.disabled = false;
      })
      .catch(() => {
        if (promise !== this.#pendingVariantChange) return;
        this.#resolvedVariantId = null;
        button.disabled = true;
      })
      .finally(() => {
        if (promise === this.#pendingVariantChange) {
          this.#pendingVariantChange = null;
        }
      });
  };

  #handleClick = async (event) => {
    event.preventDefault();

    const button = this.#button;
    const productForm = this.#productForm;
    if (!button || !productForm || button.disabled) return;

    const pending = this.#pendingVariantChange;
    if (pending) {
      try {
        await pending;
      } catch {
        return;
      }
    }

    const form = /** @type {HTMLFormElement | null} */ (productForm.querySelector('form'));
    if (!form) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('[ref="addToCartButton"]')
    );
    if (addToCartButton?.disabled) return;

    const variantId = this.#resolvedVariantId || this.#variantInput?.value;
    if (!variantId || !/^\d+$/.test(variantId)) return;

    const formData = new FormData(form);
    const quantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1);

    const root = this.dataset.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

    window.location.assign(`${normalizedRoot}cart/${encodeURIComponent(variantId)}:${quantity}`);
  };
}

if (!customElements.get('lbc-buy-now')) {
  customElements.define('lbc-buy-now', LbcBuyNow);
}
